import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import styles from './MyEventMemoriesPage.module.css';
import { useTranslation } from 'react-i18next';

const isValidStripeCheckoutUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com';
  } catch {
    return false;
  }
};

// APIレスポンス形状が揺らいでも安全に配列を取り出すヘルパー
const extractImageArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data; // Laravel Resourceコレクション標準
  if (Array.isArray(payload.images)) return payload.images; // 独自 images キー
  if (Array.isArray(payload)) return payload; // 生配列
  return [];
};

const MyEventMemoriesPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('myEventMemories');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState('personal');
  const [uploadQueue, setUploadQueue] = useState([]); // {file, optimizedFile, previewUrl, progress, status, error}
  const [processing, setProcessing] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadLimit, setUploadLimit] = useState(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [eventName, setEventName] = useState('');
  const [purchasingPlan, setPurchasingPlan] = useState(null); // 'plus5' | 'plus10' | null
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [canUpload, setCanUpload] = useState(null); // null=loading, true/false
  const [uploadBlockedReason, setUploadBlockedReason] = useState(null);

  // クエリパラメータで決済完了を検知して再取得
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gallery_limit_upgraded') === '1') {
      // URLからパラメータを除去（履歴を汚さない）
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const fetchMine = useCallback(() => {
    setLoading(true);
    setError('');
    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/my-images`)
      .then(res => {
        setImages(extractImageArray(res.data));
        if (res.data?.upload_limit != null) setUploadLimit(res.data.upload_limit);
        if (res.data?.uploaded_count != null) setUploadedCount(res.data.uploaded_count);
        if (res.data?.event_name) setEventName(res.data.event_name);
        if (res.data?.is_organizer != null) setIsOrganizer(!!res.data.is_organizer);
        setCanUpload(res.data?.can_upload === true);
        setUploadBlockedReason(res.data?.upload_blocked_reason ?? null);
      })
      .catch(() => setError(t('errFetch')))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const resizeImageFile = (file, maxSize = 2000, quality = 0.85) => new Promise(resolve => {
    const type = file.type;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width >= height) {
            const ratio = maxSize / width;
            width = maxSize;
            height = Math.round(height * ratio);
          } else {
            const ratio = maxSize / height;
            height = maxSize;
            width = Math.round(width * ratio);
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const outputType = /image\/png/i.test(type) ? 'image/png' : (/image\/webp/i.test(type) ? 'image/webp' : 'image/jpeg');
        canvas.toBlob(blob => {
          if (!blob) return resolve(file);
          const nameParts = file.name.split('.');
          const ext = nameParts.pop();
            const newName = nameParts.join('.') + '-opt.' + (outputType === 'image/jpeg' ? 'jpg' : (outputType === 'image/png' ? 'png' : 'webp'));
          const optimizedFile = new File([blob], newName, { type: outputType });
          resolve(optimizedFile);
        }, outputType, quality);
      };
      img.onerror = () => resolve(file);
      img.src = reader.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

  const handleFileSelect = async (e) => {
    uploadQueue.forEach(u => { if (u.previewUrl) URL.revokeObjectURL(u.previewUrl); });
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    if (!files.length) {
      setUploadQueue([]);
      return;
    }
    setProcessing(true);
    const processed = [];
    for (const f of files) {
      // 大きいファイルのみ縮小（閾値 ~300KB未満ならそのまま）
      let optimized = f;
      if (f.size > 300 * 1024) {
        try {
          optimized = await resizeImageFile(f);
        } catch (_) {
          optimized = f;
        }
      }
      const previewUrl = URL.createObjectURL(optimized);
      processed.push({ file: f, optimizedFile: optimized, previewUrl, progress: 0, status: 'pending', error: '' });
    }
    setUploadQueue(processed);
    setProcessing(false);
  };

  const friendlyError = (status, raw) => {
    if (status === 422) return raw || t('errUpload422');
    if (status === 403) return t('errUpload403');
    if (status === 429) return t('errUpload429');
    if (status === 500) return t('errUpload500');
    return raw || t('errUploadDefault');
  };

  const uploadSequential = async () => {
    if (!selectedFiles.length) return;
    const updated = [...uploadQueue];
    for (let i = 0; i < updated.length; i++) {
      const item = updated[i];
      if (item.status !== 'pending') continue;
      item.status = 'uploading';
      setUploadQueue([...updated]);
      const formData = new FormData();
      formData.append('visibility', visibility);
      if (caption.trim()) formData.append('caption', caption.trim());
      formData.append('images[0]', item.optimizedFile || item.file); // 圧縮済みがあればそれを使用
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${id}/memories`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            item.progress = Math.round((evt.loaded / evt.total) * 100);
            setUploadQueue([...updated]);
          }
        });
        item.progress = 100;
        item.status = 'done';
        setUploadQueue([...updated]);
        const newImages = extractImageArray(res.data.images || res.data);
        if (newImages.length) {
          setImages(prev => [...newImages, ...prev]);
        }
      } catch (err) {
        item.status = 'error';
        const status = err?.response?.status;
        const rawMsg = err?.response?.data?.message || err?.message;
        item.error = friendlyError(status, rawMsg);
        setUploadQueue([...updated]);
      }
    }
  };

  const retryFailed = (index) => {
    setUploadQueue(prev => prev.map((u,i) => i === index ? { ...u, status: 'pending', progress: 0, error: '' } : u));
  };

  const retryAllFailed = () => {
    setUploadQueue(prev => prev.map(u => u.status === 'error' ? { ...u, status: 'pending', progress: 0, error: '' } : u));
  };

  const handleDelete = (image) => {
    setDeleteTarget(image);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/events/${id}/images/${deleteTarget.id}`);
      setImages(prev => prev.filter(i => i.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const openLightbox = (index) => {
    if (!images.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handlePurchasePlan = async (planKey) => {
    setPurchasingPlan(planKey);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${id}/gallery-limit/checkout`,
        { plan: planKey }
      );
      const url = res.data?.checkout_url;
      // Stripe Checkout URL のみ許可（オープンリダイレクト対策）
      if (isValidStripeCheckoutUrl(url)) {
        window.location.href = url;
      } else {
        alert(t('alertInvalidRedirect'));
        setPurchasingPlan(null);
      }
    } catch (err) {
      alert(t('errCheckout'));
      setPurchasingPlan(null);
    }
  };

  const uploadBlockedMessage = {
    canceled:      t('blockedCanceled'),
    not_started:   t('blockedNotStarted'),
    no_permission: t('blockedNoPermission'),
  };

  const isUploading = uploadQueue.some(i => i.status === 'uploading');
  const hasErrors = uploadQueue.some(u => u.status === 'error');
  const hasPending = selectedFiles.length > 0;

  const statusLabel = (status) => {
    if (status === 'pending') return { text: t('statusPending'), cls: styles.statusPending };
    if (status === 'uploading') return { text: t('statusUploading'), cls: styles.statusUploading };
    if (status === 'done') return { text: t('statusDone'), cls: styles.statusDone };
    if (status === 'error') return { text: t('statusError'), cls: styles.statusError };
    return { text: '', cls: '' };
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        <div className={styles.pageHeader}>
          <h1 className={styles.heading}>{t('heading')}</h1>
          <p className={styles.desc}>{eventName ? t('desc', { name: eventName }) : t('descNoName')}</p>
        </div>

        {/* アップロードセクション */}
        <section className={styles.card}>
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>{t('sectionUpload')}</h2>
            {canUpload && uploadLimit != null && (
              <span className={`${styles.limitBadge} ${uploadedCount >= uploadLimit ? styles.limitReached : ''}`}>
                {t('limitBadge', { uploaded: uploadedCount, limit: uploadLimit })}
              </span>
            )}
          </div>

          {/* アップロード不可メッセージ */}
          {canUpload === false && (
            <p className={styles.errorMsg}>
              {uploadBlockedMessage[uploadBlockedReason] ?? t('blockedDefault')}
            </p>
          )}

          {/* アップロードフォーム（条件を満たした場合のみ表示） */}
          {canUpload === true && (<>

          {/* 上限解放プラン はアップロードボタン下へ移動 */}

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{t('labelVisibility')}</label>
            <select
              className={styles.select}
              value={visibility}
              onChange={e => setVisibility(e.target.value)}
            >
              <option value="personal">{t('visPersonal')}</option>
              <option value="public">
                {isOrganizer ? t('visPublicOrganizer') : t('visPublicAttendee')}
              </option>
            </select>
          </div>

          <label className={styles.fileLabel}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className={styles.fileInput}
            />
            <span className={styles.fileLabelInner}>
              <span className={styles.fileLabelIcon}>＋</span>
              {t('selectPhotos')}
            </span>
          </label>

          {processing && (
            <p className={styles.processingText}>{t('optimizing')}</p>
          )}

          {hasPending && (
            <div className={styles.previewGrid}>
              {uploadQueue.map((u, idx) => {
                const { text, cls } = statusLabel(u.status);
                return (
                  <div key={u.previewUrl || idx} className={`${styles.previewItem} ${u.status === 'done' ? styles.previewDone : ''} ${u.status === 'error' ? styles.previewError : ''}`}>
                    <div className={styles.previewImgWrap}>
                      <img src={u.previewUrl} alt="preview" className={styles.previewImg} />
                      {u.status === 'done' && <span className={styles.doneOverlay}>✓</span>}
                    </div>
                    <div className={styles.progressBarOuter}>
                      <div
                        className={`${styles.progressBarInner} ${u.status === 'error' ? styles.progressError : ''}`}
                        style={{ width: `${u.progress}%` }}
                      />
                    </div>
                    <span className={`${styles.statusBadge} ${cls}`}>{text}</span>
                    {u.error && (
                      <div className={styles.errorBlock}>
                        <p className={styles.errorMsg}>{u.error}</p>
                        <button type="button" className={styles.retryBtn} onClick={() => retryFailed(idx)}>
                          {t('retryOne')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.uploadActions}>
            <button
              className={styles.uploadBtn}
              disabled={!hasPending || isUploading}
              onClick={uploadSequential}
            >
              {isUploading ? t('uploadingBtn') : t('uploadBtn')}
            </button>
            {hasErrors && (
              <button type="button" className={styles.retryAllBtn} onClick={retryAllFailed}>
                {t('retryAll')}
              </button>
            )}
          </div>

          {/* 上限解放プラン */}
          {uploadLimit != null && (
            <div className={styles.upgradeBanner}>
              <p className={styles.upgradeLabel}>{t('upgradeBannerTitle')}</p>
              <div className={styles.planRow}>
                <button
                  type="button"
                  className={styles.planBtn}
                  onClick={() => handlePurchasePlan('plus5')}
                  disabled={!!purchasingPlan}
                >
                  {purchasingPlan === 'plus5' ? t('planProcessing') : t('planPlus5')}
                </button>
                <button
                  type="button"
                  className={styles.planBtn}
                  onClick={() => handlePurchasePlan('plus10')}
                  disabled={!!purchasingPlan}
                >
                  {purchasingPlan === 'plus10' ? t('planProcessing') : t('planPlus10')}
                </button>
              </div>
            </div>
          )}
          </>)}
        </section>

        {/* 画像一覧セクション */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>{t('sectionMyPhotos')}</h2>
          {loading && <p className={styles.stateText}>{t('loading')}</p>}
          {error && <p className={styles.errorMsg}>{error}</p>}
          {!loading && !error && images.length === 0 && (
            <p className={styles.emptyText}>{t('empty')}</p>
          )}
          <div className={styles.grid}>
            {images.map((img, idx) => (
              <div key={img.id} className={styles.cardWrapper}>
                <EventGalleryImageCard image={img} onImageClick={() => openLightbox(idx)} />
                <button className={styles.deleteBtn} onClick={() => handleDelete(img)}>
                  {t('deleteBtn')}
                </button>
              </div>
            ))}
          </div>
        </section>

      </div>

      <GalleryLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {deleteTarget && (
        <div className={styles.modalOverlay} onClick={() => !deleting && setDeleteTarget(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{t('modalTitle')}</h3>
            <p className={styles.modalDesc}>{t('modalDesc')}</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalCancelBtn}
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                {t('cancelBtn')}
              </button>
              <button
                type="button"
                className={styles.modalDeleteBtn}
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t('deletingBtn') : t('confirmDeleteBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyEventMemoriesPage;
